import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Send, Wrench, ShieldCheck, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from './api';

// Messagerie côté élève.
// Règle : l'élève ne peut PAS démarrer une conversation libre.
// Il peut seulement RÉPONDRE à un message de l'administration, ou SIGNALER un problème technique.
export default function StudentMessages({ token, onBack, onRead }) {
  const [messages, setMessages] = useState([]);
  const [canReply, setCanReply] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState('');
  const [reporting, setReporting] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await apiCall('/messages', 'GET', null, token);
      setMessages(d.messages || []);
      setCanReply(!!d.can_reply);
      if (onRead) onRead();
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [token, onRead]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const sendReply = async () => {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      await apiCall('/messages/reply', 'POST', { body }, token);
      setReply('');
      await load();
    } catch (e) { toast.error(e.message || 'Envoi impossible'); } finally { setSending(false); }
  };

  const sendReport = async () => {
    const body = report.trim();
    if (!body) { toast.error('Décris le problème.'); return; }
    setReporting(true);
    try {
      await apiCall('/messages/report', 'POST', { body }, token);
      setReport(''); setShowReport(false);
      toast.success('Signalement envoyé à l\'administration ✅');
      await load();
    } catch (e) { toast.error(e.message || 'Envoi impossible'); } finally { setReporting(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto px-4 py-8" data-testid="student-messages">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-white/60 hover:text-white mb-5" data-testid="messages-back">
        <ChevronLeft size={16} />Retour
      </button>

      <h2 className="text-2xl font-bold flex items-center gap-2 mb-1"><Inbox size={22} className="text-emerald-400" />Messages</h2>
      <p className="text-sm text-white/50 mb-6">Échange directement avec l'administration de JoSmartTech Academy, ici dans l'application.</p>

      {/* Conversation */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5 min-h-[180px]">
        {loading ? (
          <div className="text-white/50 text-sm py-6 text-center">Chargement…</div>
        ) : messages.filter(m => m.kind !== 'report').length === 0 ? (
          <div className="text-center py-8" data-testid="messages-empty">
            <ShieldCheck size={28} className="text-emerald-400/70 mx-auto mb-3" />
            <p className="text-sm text-white/60">Aucun message pour le moment.</p>
            <p className="text-xs text-white/40 mt-1">L'administration te contactera ici pour tes formations et ton suivi.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.filter(m => m.kind !== 'report').map((m) => (
              <div key={m.id} className={`flex ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.sender}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.sender === 'user' ? 'bg-emerald-600/80 text-white rounded-br-sm' : 'bg-white/10 text-white/90 rounded-bl-sm'}`}>
                  {m.sender === 'admin' && <div className="text-[10px] font-bold text-emerald-300 mb-0.5">Administration</div>}
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  <div className="text-[9px] text-white/40 mt-1 text-right">{(m.created_at || '').slice(0, 16).replace('T', ' ')}</div>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Réponse — uniquement si l'administration a déjà écrit */}
      {canReply ? (
        <div className="flex items-end gap-2 mb-8">
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={2} placeholder="Écris ta réponse…"
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-400/50" data-testid="reply-input" />
          <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="reply-send"
            className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-slate-950 font-bold px-4 py-2.5 rounded-xl flex items-center gap-1">
            <Send size={15} />
          </button>
        </div>
      ) : (
        <p className="text-xs text-white/40 mb-8 text-center">Tu pourras répondre ici dès que l'administration t'aura écrit.</p>
      )}

      {/* Signalement d'un problème technique — seul canal que l'élève peut initier */}
      <div className="bg-amber-500/[0.06] border border-amber-400/20 rounded-2xl p-4" data-testid="report-section">
        <div className="flex items-center gap-2 text-amber-200 font-semibold text-sm"><Wrench size={16} />Un problème technique ?</div>
        <p className="text-xs text-white/55 mt-1">Un bug, une page qui ne s'ouvre pas, un son qui ne marche pas… décris-le et on s'en occupe.</p>
        {!showReport ? (
          <button onClick={() => setShowReport(true)} data-testid="report-open"
            className="mt-3 bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-sm font-semibold px-4 py-2 rounded-lg">
            Signaler un problème technique
          </button>
        ) : (
          <div className="mt-3 space-y-2">
            <textarea value={report} onChange={e => setReport(e.target.value)} rows={3} placeholder="Décris le problème rencontré…"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-amber-400/50" data-testid="report-input" />
            <div className="flex gap-2">
              <button onClick={sendReport} disabled={reporting || !report.trim()} data-testid="report-send"
                className="bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-slate-950 font-bold px-4 py-2 rounded-lg text-sm">
                Envoyer le signalement
              </button>
              <button onClick={() => { setShowReport(false); setReport(''); }} className="text-white/50 hover:text-white text-sm px-3">Annuler</button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
